/**
 * NoDataMessage Component
 *
 * Displays a friendly empty state message when there's no data to show.
 * Used by CustomDatagrid and can be used standalone.
 */

import React from 'react';
import { Box, Typography, useTheme } from '@mui/material';
import { InboxOutlined } from '@mui/icons-material';

interface NoDataMessageProps {
  title?: string;
  description?: string;
  icon?: React.ReactNode;
}

const NoDataMessage: React.FC<NoDataMessageProps> = ({
  title = 'No data found',
  description = 'There are no records to display.',
  icon,
}) => {
  const theme = useTheme();

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        py: 8,
        px: 2,
        textAlign: 'center',
      }}
    >
      <Box
        sx={{
          width: 80,
          height: 80,
          borderRadius: '50%',
          backgroundColor: theme.palette.grey[100],
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          mb: 3,
        }}
      >
        {icon || (
          <InboxOutlined
            sx={{
              fontSize: 40,
              color: theme.palette.grey[400],
            }}
          />
        )}
      </Box>

      <Typography
        variant="h6"
        sx={{
          fontWeight: 600,
          color: theme.palette.text.primary,
          mb: 1,
        }}
      >
        {title}
      </Typography>

      <Typography
        variant="body2"
        sx={{
          color: theme.palette.text.secondary,
          maxWidth: 400,
        }}
      >
        {description}
      </Typography>
    </Box>
  );
};

export default NoDataMessage;
